import {Trans} from '@lingui/macro';
import fuzzysort from 'fuzzysort';
import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {
  fuzzySortBaseOptions,
  SearchItemProps,
  SearchToken,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSearchSuggestionItemProps,
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItem,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownItemPrimaryContent,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Constants.
 */

const propertiesModifiers = [
  ModifiersEnum.TYPE,
  ModifiersEnum.HAS,
  ModifiersEnum.IS,
  ModifiersEnum.IN,
  ModifiersEnum.BEFORE,
  ModifiersEnum.AFTER,
  ModifiersEnum.DURING,
];
const TypeValues = ['email', 'facebook', 'googleplay', 'intercom', 'sms', 'tweet', 'tweet_dm'];
const HasValues = ['attachment', 'no-tags', 'reminder', 'calendar', 'draft'];
const IsValues = [
  'assigned',
  'archived',
  'closed',
  'deleted',
  'discussion',
  'muted',
  'open',
  'shared-with-me',
  'snoozed',
  'spam',
  'starred',
  'subscribed',
  'tracking',
  'trashed',
  'unassigned',
  'unread',
  'unreplied',
];
const InValues = ['all', 'comments', 'messages', 'attachments'];
const CommonDatesValues = ['last-week', 'yyyy-mm-dd', 'yyyy-mm'];

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownPropertiesProps extends InboxesSearchSuggestionsBaseProps {}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownProperties: FC<
  InboxesSearchSuggestionsDropdownPropertiesProps
> = (props) => {
  const {activeToken, onSuggestionSelect} = props;

  const items = searchItems(activeToken);

  if (items.length === 0) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-search-properties">Suggestions</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
        {items.map((item) => (
          <InboxesSuggestionsDropdownItem
            onSelect={(e, key) =>
              onSuggestionSelect(item.value, key, {insertWhiteSpace: true, isModifierSuggestion: true})
            }
            key={item.value}
          >
            <InboxesSuggestionsDropdownItemIcon iconName="searchSmall" />
            <InboxesSuggestionsDropdownItemPrimaryContent>
              {item.primaryLabel}
            </InboxesSuggestionsDropdownItemPrimaryContent>
          </InboxesSuggestionsDropdownItem>
        ))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function searchItems(activeToken: SearchToken) {
  // Return empty array when search conditions are not found
  if (isUndefined(activeToken.modifier) || !propertiesModifiers.includes(activeToken.modifier)) {
    return [];
  }

  const propertiesItem = getPropertiesList(activeToken);

  return fuzzySort(activeToken, propertiesItem);
}

function getPropertiesList(activeToken: SearchToken): Array<InboxesSearchSuggestionItemProps> {
  const values = getPropertiesValues(activeToken.modifier);

  return values.map((primaryLabel) => ({
    value: `${activeToken.modifier}:${primaryLabel}`,
    primaryLabel: `${activeToken.modifier}: ${primaryLabel}`,
  }));
}

function getPropertiesValues(modifier: ModifiersEnum | undefined): Array<string> {
  switch (modifier) {
    case ModifiersEnum.TYPE:
      return TypeValues;

    case ModifiersEnum.HAS:
      return HasValues;

    case ModifiersEnum.IS:
      return IsValues;

    case ModifiersEnum.IN:
      return InValues;

    case ModifiersEnum.BEFORE:
    case ModifiersEnum.DURING:
      return ['today', ...CommonDatesValues];

    case ModifiersEnum.AFTER:
      return ['yesterday', ...CommonDatesValues];

    default:
      throw new Error('Property modifier does not exist');
  }
}

function fuzzySort(
  activeToken: SearchToken,
  propertiesItem: Array<InboxesSearchSuggestionItemProps>,
): Array<SearchItemProps> {
  if (isUndefined(activeToken.value) || !activeToken.value.length) {
    return propertiesItem;
  }

  return fuzzysort
    .go(activeToken.value, propertiesItem, {
      ...fuzzySortBaseOptions,
      key: 'primaryLabel',
    })
    .map((item) => ({
      value: item.obj.value,
      primaryLabel: <FuzzyHighlight text={item.obj.primaryLabel as string} fuzzy={item} />,
    }));
}
